import {
  TerraDraw,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

export const ensureMapLibrary = async (maps: any, library: string): Promise<void> => {
  if (maps[library]) {
    return;
  }
  if (typeof maps.importLibrary === 'function') {
    await maps.importLibrary(library);
  }
};

type DrawingMode = 'polygon' | 'polyline' | 'rectangle' | null;
type DrawingEvent = 'polygoncomplete' | 'polylinecomplete' | 'rectanglecomplete';

const TERRA_MODE_MAP: Record<Exclude<DrawingMode, null>, string> = {
  polygon: 'polygon',
  polyline: 'linestring',
  rectangle: 'rectangle',
};

const waitForMapProjection = (maps: any, map: any): Promise<void> => {
  return new Promise((resolve) => {
    if (map.getProjection?.()) {
      resolve();
      return;
    }
    maps.event.addListenerOnce(map, 'projection_changed', () => resolve());
  });
};

const coordinatesToLatLngs = (maps: any, coordinates: number[][]): any[] => {
  return coordinates.map(([lng, lat]) => new maps.LatLng(lat, lng));
};

export class MapDrawingController {
  private draw: TerraDraw | null = null;
  private maps: any;
  private activeMode: DrawingMode = null;
  private keepDrawingActive = false;
  private ready = false;
  private listeners: Partial<Record<DrawingEvent, ((overlay: any) => void)[]>> = {};

  private constructor(maps: any) {
    this.maps = maps;
  }

  static async create(maps: any, map: any): Promise<MapDrawingController | null> {
    if (!maps || !map) {
      return null;
    }

    await waitForMapProjection(maps, map);

    const controller = new MapDrawingController(maps);
    const initialized = await controller.init(map);
    return initialized ? controller : null;
  }

  private init(map: any): Promise<boolean> {
    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => resolve(false), 10000);

      const finishInit = (success: boolean) => {
        window.clearTimeout(timeout);
        resolve(success);
      };

      try {
        this.draw = new TerraDraw({
          adapter: new TerraDrawGoogleMapsAdapter({
            map,
            lib: this.maps,
          }),
          modes: [
            new TerraDrawPolygonMode(),
            new TerraDrawLineStringMode(),
            new TerraDrawRectangleMode(),
          ],
        });

        this.draw.on('ready', () => {
          this.ready = true;
          this.draw?.setMode('static');
          finishInit(true);
        });

        this.draw.on('finish', (id: string, context: { action: string; mode: string }) => {
          if (context.action !== 'draw') {
            return;
          }
          this.handleFinish(id);
        });

        // start() must run after listeners are registered; it triggers the adapter ready event.
        this.draw.start();
      } catch {
        finishInit(false);
      }
    });
  }

  private handleFinish(featureId: string): void {
    if (!this.draw || !this.activeMode) {
      return;
    }

    const event = this.modeToEvent(this.activeMode);
    if (!event) {
      return;
    }

    const feature = this.draw.getSnapshotFeature(featureId);
    if (!feature) {
      return;
    }

    const overlay = this.createLegacyOverlay(featureId, feature, event);
    const callbacks = this.listeners[event] || [];
    callbacks.forEach((callback) => callback(overlay));

    // Consumers usually call overlay.setMap(null) inside the callback above,
    // which already removes the feature. Guard against removing it a second
    // time here — terra-draw throws "feature with id ... can not delete" when
    // asked to delete a feature that is already gone, and that throw escapes
    // the 'finish' event handler and freezes the map interaction.
    this.removeFeatureIfPresent(featureId);

    if (this.keepDrawingActive && this.activeMode) {
      this.draw.setMode(TERRA_MODE_MAP[this.activeMode]);
      return;
    }

    this.draw.setMode('static');
    this.activeMode = null;
    this.keepDrawingActive = false;
  }

  private removeFeatureIfPresent(featureId: string): void {
    if (!this.draw) {
      return;
    }
    // getSnapshotFeature returns undefined once the feature has been removed,
    // so this makes feature removal idempotent across the legacy overlay's
    // setMap(null) and handleFinish.
    if (!this.draw.getSnapshotFeature(featureId)) {
      return;
    }
    this.draw.removeFeatures([featureId]);
  }

  private modeToEvent(mode: DrawingMode): DrawingEvent | null {
    if (mode === 'polygon') {
      return 'polygoncomplete';
    }
    if (mode === 'polyline') {
      return 'polylinecomplete';
    }
    if (mode === 'rectangle') {
      return 'rectanglecomplete';
    }
    return null;
  }

  private createLegacyOverlay(featureId: string, feature: any, event: DrawingEvent): any {
    const removeFromMap = () => {
      this.removeFeatureIfPresent(featureId);
    };

    if (event === 'polylinecomplete') {
      const linePath = coordinatesToLatLngs(this.maps, feature.geometry.coordinates);
      return {
        getPath: () => ({
          getArray: () => linePath,
        }),
        setMap: removeFromMap,
      };
    }

    const ring = feature.geometry.coordinates[0] as number[][];
    const ringPath = coordinatesToLatLngs(this.maps, ring);

    if (event === 'rectanglecomplete') {
      const lats = ring.map((coord) => coord[1]);
      const lngs = ring.map((coord) => coord[0]);
      const sw = new this.maps.LatLng(Math.min(...lats), Math.min(...lngs));
      const ne = new this.maps.LatLng(Math.max(...lats), Math.max(...lngs));
      return {
        getBounds: () => ({
          getSouthWest: () => sw,
          getNorthEast: () => ne,
        }),
        setMap: removeFromMap,
      };
    }

    return {
      getPath: () => ({
        getArray: () => ringPath,
      }),
      setMap: removeFromMap,
    };
  }

  isInitialized(): boolean {
    return this.ready && this.draw !== null;
  }

  setDrawingMode(mode: DrawingMode, options?: { keepActive?: boolean }): void {
    this.activeMode = mode;
    this.keepDrawingActive = options?.keepActive === true;
    if (!this.draw || !this.ready) {
      return;
    }
    if (!mode) {
      this.keepDrawingActive = false;
      this.draw.setMode('static');
      return;
    }
    this.draw.setMode(TERRA_MODE_MAP[mode]);
  }

  addListener(event: DrawingEvent, callback: (overlay: any) => void): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event]?.push(callback);
  }

  clearListeners(event?: DrawingEvent): void {
    if (event) {
      delete this.listeners[event];
      return;
    }
    this.listeners = {};
  }
}

export const ensureDrawingManager = async (maps: any, map: any, existing: any): Promise<MapDrawingController | null> => {
  if (existing instanceof MapDrawingController && existing.isInitialized()) {
    return existing;
  }
  return MapDrawingController.create(maps, map);
};

// Dark InfoWindow: one semi-transparent shell; content stays transparent.
const MAP_IW_BG = 'rgba(0, 0, 0, 0.8)';

let darkIwCssInjected = false;

const injectDarkInfoWindowCss = (): void => {
  if (darkIwCssInjected || typeof document === 'undefined') {
    return;
  }
  darkIwCssInjected = true;
  const style = document.createElement('style');
  style.id = 'ceres-dark-infowindow-css';
  style.textContent = `
    .gm-style .gm-style-iw-c {
      background: ${MAP_IW_BG} !important;
      background-color: ${MAP_IW_BG} !important;
      border: none !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35) !important;
      border-radius: 6px !important;
      padding: 0 !important;
      overflow: hidden !important;
    }
    .gm-style .gm-style-iw-d {
      overflow: hidden !important;
      max-height: none !important;
      padding-right: 0 !important;
      background: transparent !important;
      background-color: transparent !important;
    }
    .gm-style .gm-style-iw-chr {
      background: transparent !important;
      background-color: transparent !important;
    }
    .gm-style .gm-style-iw-tc::after {
      background: ${MAP_IW_BG} !important;
      background-color: ${MAP_IW_BG} !important;
    }
    .ceres-map-infowindow,
    .ceres-map-infowindow p,
    .ceres-map-infowindow b {
      background: transparent !important;
      background-color: transparent !important;
      color: #fff !important;
    }
  `;
  document.head.appendChild(style);
};

const buildDarkInfoContent = (innerHtml: string): HTMLDivElement => {
  const root = document.createElement('div');
  root.className = 'ceres-map-infowindow';
  root.style.cssText = 'background:transparent;color:#fff;font-size:12px;line-height:1.45;padding:8px 12px;box-sizing:border-box;';
  root.innerHTML = innerHtml;
  return root;
};

/** Override Google's white bubble after open (only InfoWindow chrome, not the map). */
const paintInfoWindowShellDark = (contentEl: HTMLElement, closeButtonColor?: string): void => {
  try {
    const bubble = contentEl.closest('.gm-style-iw-c') as HTMLElement | null;
    if (!bubble) {
      return;
    }
    bubble.style.setProperty('background', MAP_IW_BG, 'important');
    bubble.style.setProperty('background-color', MAP_IW_BG, 'important');
    bubble.style.setProperty('border', 'none', 'important');
    bubble.style.setProperty('border-radius', '6px', 'important');
    bubble.style.setProperty('padding', '0', 'important');
    bubble.style.setProperty('overflow', 'hidden', 'important');
    contentEl.closest('.gm-style-iw')?.querySelectorAll('.gm-style-iw-d').forEach((node) => {
      const el = node as HTMLElement;
      el.style.setProperty('overflow', 'hidden', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('padding-right', '0', 'important');
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
    });
    contentEl.closest('.gm-style-iw')?.querySelectorAll('.gm-style-iw-chr').forEach((node) => {
      const el = node as HTMLElement;
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
    });

    const host = contentEl.closest('.gm-style-iw')?.parentElement;
    host?.querySelectorAll('.gm-style-iw-tc').forEach((tip) => {
      (tip as HTMLElement).style.setProperty('background', MAP_IW_BG, 'important');
      (tip as HTMLElement).style.setProperty('background-color', MAP_IW_BG, 'important');
    });

    // Recolor Google's default close button when the caller asks for it; leave
    // as-is otherwise. Google renders the icon as a masked <span> or an <img>,
    // depending on the Maps version, so cover both.
    if (closeButtonColor) {
      contentEl.closest('.gm-style-iw')?.querySelectorAll('.gm-ui-hover-effect').forEach((node) => {
        (node as HTMLElement).querySelectorAll('span').forEach((span) => {
          (span as HTMLElement).style.setProperty('background-color', closeButtonColor, 'important');
        });
        (node as HTMLElement).querySelectorAll('img').forEach((img) => {
          (img as HTMLElement).style.setProperty('filter', 'brightness(0) invert(1)', 'important');
        });
      });
    }
  } catch {
    // ignore
  }
};

const PAINT_IW_RETRY_MS = [0, 50, 200, 500];

/**
 * Dark-themed InfoWindow. Use with openInfoWindow(iw, map).
 * Pass `closeButtonColor` to recolor Google's default close button; when omitted
 * the close button keeps Google's default color.
 */
export const createDarkInfoWindow = (
  maps: any,
  innerHtml: string,
  position: any,
  closeButtonColor?: string,
): any => {
  injectDarkInfoWindowCss();
  const contentEl = buildDarkInfoContent(innerHtml);
  const infoWindow = new maps.InfoWindow({ content: contentEl, position });
  maps.event.addListener(infoWindow, 'domready', () => {
    PAINT_IW_RETRY_MS.forEach((delay) => {
      window.setTimeout(() => paintInfoWindowShellDark(contentEl, closeButtonColor), delay);
    });
  });
  return infoWindow;
};

/** Open InfoWindow (Maps beta + legacy). */
export const openInfoWindow = (infoWindow: any, map: any): void => {
  if (!infoWindow || !map) {
    return;
  }
  try {
    infoWindow.open({ map, shouldFocus: false });
  } catch {
    infoWindow.open(map);
  }
};
